import { Box, Button, Grid, Typography, useTheme } from "@mui/material";
import { Ico_Export, Ico_Eye, Ico_Plus } from "@sagecentral/core-ui";
import { useWorkItemContext, useWorkItemPermissions } from "hooks/work-items";
import { useCallback, useEffect, useRef, useState } from "react";
import useWorkItemWorkflows from "hooks/work-items/useWorkItemWorkflows";
import { CustomCircularProgress } from "components/shared/layout";
import IWorkItemWorkflow from "models/work-items/IWorkItemWorkflow";
import useWorkItemWorkflowsExport from "hooks/work-items/useWorkItemWorkflowsExport";
import WorkflowTasksCard from "components/workflows/tasks/WorkflowTasksCard";

const Workflows = () => {
    const [showAll, setShowAll] = useState<boolean | undefined>(undefined);
    const [expandedWorkflows, setExpandedWorkflows] = useState<Set<number>>(new Set());
    const [lastFocusedWorkflow, setLastFocusedWorkflow] = useState<number | null>(null);
    const workflowRefs = useRef<Map<number, HTMLElement | null>>(new Map());
    
    const theme = useTheme();
    const { workItem } = useWorkItemContext();
    const [data, total, loading, error, refresh] = useWorkItemWorkflows(workItem?.id ?? 0);
    const workFlowCreateUrl = `${process.env.REACT_APP_CLASSIC_URL}/WorkflowInstance/CreateWorkflowInstance?workItemID=${workItem?.id}`;
    
    const handleRefresh = useCallback(() => {
        const currentExpandedState = new Set(expandedWorkflows);
        refresh();
        
        setTimeout(() => {
            setExpandedWorkflows(currentExpandedState);
            
            // Restore focus if we have a last focused workflow
            if (lastFocusedWorkflow) {
                const workflowElement = workflowRefs.current.get(lastFocusedWorkflow);
                if (workflowElement) {
                    workflowElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                }
            }
        }, 100);
    }, [expandedWorkflows, lastFocusedWorkflow, refresh]);
    
    const handleWorkflowExpandToggle = useCallback((workflowId: number, isExpanded: boolean) => {
        setExpandedWorkflows(prev => {
            const newSet = new Set(prev);
            if (isExpanded) {
                newSet.add(workflowId);
            } else {
                newSet.delete(workflowId);
            }
            return newSet;
        });
        setLastFocusedWorkflow(workflowId);
    }, []);
    
    useEffect(() => {
        if (showAll === undefined) return;
        
        if (showAll && data?.workflows) {
            setExpandedWorkflows(new Set(data.workflows.map(wf => wf.id)));
        } else {
            setExpandedWorkflows(new Set());
        }
    }, [showAll, data?.workflows]);

    const permissions = useWorkItemPermissions();

    const canActivateWf = permissions?.canActivateWorkflows ?? false;
    const canDeactivateWf = permissions?.canDeactivateWorkflows ?? false;

    const handleExport = useWorkItemWorkflowsExport(workItem?.id ?? 0, workItem?.name ?? "");
    const hasAccessToCreateWF = data?.hasAccessToCreateWorkflow;
    return (
        <Grid container>
            <Grid item xs={12}>
                {hasAccessToCreateWF && <Box sx={{ justifyContent: 'start' }}>
                    <Button sx={{ 
                            width: '220px', 
                            height: '40px' 
                        }} 
                        variant="outlined" 
                        href={workFlowCreateUrl}
                        startIcon={<Ico_Plus />}
                    >
                        Add New Workflow
                    </Button>
                </Box>}

                {data?.workflows.length > 0 ?
                    <Box sx={{ display: 'flex', justifyContent: 'end', mt: '12px', mb: '12px', gap: '24px' }}>
                        <Button
                            aria-label="Export workflows"
                            sx={{
                                fontSize: '14px',
                                gap: '8px',
                                color: theme.palette.common.black,
                                cursor: 'pointer',
                                p: 0,
                                minWidth: 'auto',
                                '&:focus-visible': {
                                    outline: `2px solid ${theme.palette.primary.main}`,
                                    outlineOffset: '2px'
                                }
                            }}
                            onClick={handleExport}
                            startIcon={<svg fill="none" height={24} width={24}>{Ico_Export}</svg>}
                        >
                            Export
                        </Button>
                        <Button
                            aria-label={showAll ? "Collapse all workflows" : "Show all workflows"}
                            aria-expanded={showAll}
                            sx={{
                                fontSize: '14px',
                                display: 'flex',
                                gap: '5px',
                                color: theme.palette.common.black,
                                cursor: 'pointer',
                                p: 0,
                                minWidth: 'auto',
                                '&:focus-visible': {
                                    outline: `2px solid ${theme.palette.primary.main}`,
                                    outlineOffset: '2px'
                                }
                            }}
                            onClick={() => { setShowAll(!showAll) }}
                            startIcon={Ico_Eye}
                        >
                            {!showAll ? "Show all" : "Collapse all"}
                        </Button>
                    </Box>
                    : null}

                {!loading && data?.workflows.length > 0 ?
                    data?.workflows.map((workflow: IWorkItemWorkflow) => {
                        return (
                            <Box 
                                key={workflow.id}
                                sx={{ mt: '20px' }}
                                ref={(el: HTMLDivElement | null) => {
                                    if (el) workflowRefs.current.set(workflow.id, el);
                                }}
                            >
                                <WorkflowTasksCard 
                                    workflow={workflow} 
                                    canEdit={data.hasAccessToEditWorkflow} 
                                    canActivate={canActivateWf} 
                                    canDeactivate={canDeactivateWf} 
                                    isExpanded={expandedWorkflows.has(workflow.id)}
                                    onExpandToggle={(isExpanded: boolean) => handleWorkflowExpandToggle(workflow.id, isExpanded)}
                                    refresh={handleRefresh}
                                />
                            </Box>
                        );
                    })
                    : !loading && data?.workflows.length == 0 ? <Box sx={{ mt: '20px' }}>No workflows exist</Box> : <CustomCircularProgress />}
            </Grid>
        </Grid >
    );
}

export default Workflows;

